'use client';

// Consumer Intake Flow Hook (Story 7.6)
// Manages intake questions state, response submission, and confirmation
// Note: Consumer app does not use TanStack Query - uses plain fetch/state

import { useState, useCallback, useEffect, useRef } from 'react';

interface IntakeQuestionConsumer {
  id: string;
  questionText: string;
  sortOrder: number;
}

interface IntakeFlowState {
  status: 'idle' | 'prompting' | 'asking' | 'confirming' | 'completed' | 'dismissed';
  currentQuestionIndex: number;
  answers: Map<string, string>;
}

export function useIntakeFlow(botId: string, sessionId: string) {
  const [state, setState] = useState<IntakeFlowState>({
    status: 'idle',
    currentQuestionIndex: 0,
    answers: new Map(),
  });
  const [questions, setQuestions] = useState<IntakeQuestionConsumer[]>([]);
  const [promptMessage, setPromptMessage] = useState<string | null>(null);
  const [completionMessage, setCompletionMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fetchedRef = useRef(false);

  // Check if already completed/dismissed in this session
  useEffect(() => {
    const key = `intake_${botId}_done`;
    if (typeof window !== 'undefined' && localStorage.getItem(key)) {
      setState((prev) => ({ ...prev, status: 'dismissed' }));
    }
  }, [botId]);

  // Fetch intake questions once (skip if botId is empty, e.g. intake disabled)
  useEffect(() => {
    if (fetchedRef.current || !botId || !sessionId) return;
    fetchedRef.current = true;

    fetch(`/api/bots/${botId}/intake-questions`)
      .then((res) => {
        if (!res.ok) return { data: [] };
        return res.json();
      })
      .then((result: { data: IntakeQuestionConsumer[]; promptMessage?: string | null }) => {
        setQuestions(result.data || []);
        if (result.promptMessage) setPromptMessage(result.promptMessage);
      })
      .catch(() => {
        // Silently fail - intake is non-critical
      });
  }, [botId, sessionId]);

  const currentQuestion = questions[state.currentQuestionIndex] || null;
  const hasQuestions = questions.length > 0;

  const markDone = useCallback(() => {
    const key = `intake_${botId}_done`;
    if (typeof window !== 'undefined') {
      localStorage.setItem(key, '1');
    }
  }, [botId]);

  const startPrompt = useCallback(() => {
    if (state.status !== 'idle' || !hasQuestions) return;
    setState((prev) => ({ ...prev, status: 'prompting' }));
  }, [state.status, hasQuestions]);

  const acceptPrompt = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'asking', currentQuestionIndex: 0 }));
  }, []);

  const dismissPrompt = useCallback(() => {
    setState((prev) => ({ ...prev, status: 'dismissed' }));
    markDone();
  }, [markDone]);

  const answerQuestion = useCallback(
    async (responseText: string) => {
      if (!currentQuestion) return;

      setIsSubmitting(true);
      try {
        await fetch(`/api/bots/${botId}/intake-responses`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            questionId: currentQuestion.id,
            responseText,
            sessionId,
          }),
        });
      } catch {
        // Non-critical, continue the flow
      }
      setIsSubmitting(false);

      const nextIndex = state.currentQuestionIndex + 1;
      const isLast = nextIndex >= questions.length;

      setState((prev) => {
        const newAnswers = new Map(prev.answers);
        newAnswers.set(currentQuestion.id, responseText);
        return { status: isLast ? 'confirming' : 'asking', currentQuestionIndex: nextIndex, answers: newAnswers };
      });

      // Auto-submit on last question — skip the confirmation step
      if (isLast) {
        setIsSubmitting(true);
        try {
          const res = await fetch(`/api/bots/${botId}/intake-responses/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          if (res.ok) {
            const result = await res.json();
            setCompletionMessage(result.data.completionMessage);
            setState((prev) => ({ ...prev, status: 'completed' }));
            markDone();
          }
        } catch {
          // Non-critical
        }
        setIsSubmitting(false);
      }
    },
    [currentQuestion, state.currentQuestionIndex, questions.length, botId, sessionId, markDone]
  );

  const skipQuestion = useCallback(async () => {
    const nextIndex = state.currentQuestionIndex + 1;
    const isLast = nextIndex >= questions.length;

    if (isLast) {
      if (state.answers.size > 0) {
        setState((prev) => ({ ...prev, status: 'confirming', currentQuestionIndex: nextIndex }));
        setIsSubmitting(true);
        try {
          const res = await fetch(`/api/bots/${botId}/intake-responses/confirm`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId }),
          });
          if (res.ok) {
            const result = await res.json();
            setCompletionMessage(result.data.completionMessage);
            setState((prev) => ({ ...prev, status: 'completed' }));
            markDone();
          }
        } catch {
          // Non-critical
        }
        setIsSubmitting(false);
      } else {
        setState((prev) => ({ ...prev, status: 'dismissed' }));
        markDone();
      }
    } else {
      setState((prev) => ({ ...prev, status: 'asking', currentQuestionIndex: nextIndex }));
    }
  }, [state.currentQuestionIndex, state.answers.size, questions.length, botId, sessionId, markDone]);

  const confirmSendToCreator = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`/api/bots/${botId}/intake-responses/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      if (res.ok) {
        const result = await res.json();
        setCompletionMessage(result.data.completionMessage);
        setState((prev) => ({ ...prev, status: 'completed' }));
        markDone();
      }
    } catch {
      // Non-critical
    }
    setIsSubmitting(false);
  }, [botId, sessionId, markDone]);

  const declineSend = useCallback(() => {
    // Cancel goes back to idle — flow can re-trigger after another inactivity period
    setState((prev) => ({ ...prev, status: 'idle', currentQuestionIndex: 0, answers: new Map() }));
  }, []);

  const goBack = useCallback(() => {
    if (state.currentQuestionIndex > 0) {
      setState((prev) => ({ ...prev, status: 'asking', currentQuestionIndex: prev.currentQuestionIndex - 1 }));
    }
  }, [state.currentQuestionIndex]);

  return {
    state,
    questions,
    currentQuestion,
    hasQuestions,
    promptMessage,
    completionMessage,
    isSubmitting,
    startPrompt,
    acceptPrompt,
    dismissPrompt,
    answerQuestion,
    skipQuestion,
    confirmSendToCreator,
    declineSend,
    goBack,
  };
}
